<template>
    <div class="payment-view__content">
        <template v-if="payment">
            <transition name="fade" mode="out-in">
                <template v-if="paymentError">
                    <PaymentError :payment="payment"/>
                </template>
                <template v-else>
                    <transition name="fade" mode="out-in">
                        <template v-if="payment.status === BuyingOperationStatus.CREATED">
                            <PaymentStatusCreated
                                @cancelPayment="cancelFindAgent"
                                :cancel="true"
                                :start-time="staticTime"
                                :current-time="time"
                            />
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.ACCEPTED">
                            <div class="wrapper-elements">
                                <PaymentStatusAccepted
                                    :payment="payment"
                                    :timeText="timeText"
                                    :is-amount-was-changed="isAmountWasChanged"
                                    :is-amount-was-changed-show-block-warning="isAmountWasChangedShowBlockWarning"
                                    :is-transgran-payment="isTransgranPayment"
                                    :is-transgran-payment-show-block-warning="isTransgranPaymentShowBlockWarning"
                                    :percent="percent"
                                    @cancelPayment="cancelFindAgent"
                                />
                            </div>
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.CASHED">
                            <PaymentStatusCashed
                                :payment="payment"
                                @uploadCheckImages="uploadCheckImages"
                                @uploadPersonalData="uploadPersonalData"
                            />
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.EXPIRED">
                            <PaymentStatusExpired
                                :payment="payment"
                                @submit="recreatePayment"
                                :timer-service="timerService"
                                @return-to-site="handleReturnToSite"
                            />
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.CONFIRMED">
                            <PaymentStatusConfirmed :payment="payment"/>
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.CANCELED">
                            <PaymentStatusCanceled :payment="payment"/>
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.ONAPPEAL">
                            <PaymentStatusOnappeal :payment="payment" @toChat="toChat"/>
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.REJECTED">
                            <PaymentStatusRejected :payment="payment"/>
                        </template>
                        <template v-else-if="payment.status === BuyingOperationStatus.EMPTY">
                            <PaymentStatusEmpty :payment="payment"/>
                        </template>
                    </transition>
                </template>
            </transition>
        </template>
    </div>
</template>

<script setup lang="ts">
import {onMounted, PropType, toRef, UnwrapRef, watch} from "vue";
import PaymentStatusCreated from "./contents/PaymentStatusCreated.vue";
import PaymentStatusTimer from "./atomic/PaymentStatusTimer.vue";
import PaymentStatusCanceled from "./contents/PaymentStatusCanceled.vue";
import PaymentStatusAccepted from "./contents/PaymentStatusAccepted.vue";
import PaymentStatusConfirmed from "./contents/PaymentStatusConfirmed.vue";
import PaymentStatusRejected from "./contents/PaymentStatusRejected.vue";
import {BuyingOperationStatus} from "@enums/BuyingOperationStatus";
import PurchaseOperation from "@models/operations/PurchaseOperation";
import PaymentTimerService from "@/services/operations/payment/PaymentTimerService";
import PaymentStatusCashed from "@/views/payment/components/contents/PaymentStatusCashed.vue";
import PaymentStatusOnappeal from "@/views/payment/components/contents/PaymentStatusOnappeal.vue";
import PaymentStatusExpired from "../components/contents/PaymentStatusExpired.vue";
import PaymentStatusEmpty from "@/targets/light/views/payment/components/contents/PaymentStatusEmpty.vue";
import PaymentError from "@/targets/light/views/payment/components/contents/error/PaymentError.vue";
import LightPaymentPidResponseError from "@models/responses/light-payment-pid/LightPaymentPidResponseError";
// @ts-ignore
import AppController from "@target/components/App/ts/AppController";
import PaymentCheckImage from "@models/operations/payment/PaymentCheckImage";
import PaymentStatusLoader from "@components/atomic/payment-status-loader/PaymentStatusLoader.vue";

const props = defineProps({
    payment: {
        type: Object as PropType<PurchaseOperation>,
        required: true
    },
    isAmountWasChanged: {
        type: [Boolean] as PropType<boolean>,
        required: true
    },
    isAmountWasChangedShowBlockWarning: {
        type: [Boolean] as PropType<boolean>,
        required: true
    },
    isTransgranPayment: {
        type: [Boolean] as PropType<boolean>,
        required: true
    },
    isTransgranPaymentShowBlockWarning: {
        type: [Boolean] as PropType<boolean>,
        required: true
    },
    timerService: {
        type: Object as PropType<PaymentTimerService>,
        required: true
    }
});

const payment = toRef(props, 'payment');
const paymentError: LightPaymentPidResponseError = AppController.getInstance().paymentService.paymentError;
const timerService: PaymentTimerService = props.timerService;
const timeText = timerService.getTimeText;
const percent = timerService.getPercent;
const time = timerService.time;
const staticTime = timerService.staticTime;
const emits = defineEmits([
	'timerExpired',
	'cancelPayment',
	'toChat',
	'recreatePayment',
	'uploadCheckImages',
	'uploadPersonalData',
	'returnToSite'
]);

function uploadPersonalData(message: string, callback: Function, failedCallback: Function) {
    emits("uploadPersonalData", message, callback, failedCallback)
}

const recreatePayment = (callback: Function) => {
    emits('recreatePayment', callback);
}

function uploadCheckImages(checkImages: PaymentCheckImage[], callback: Function, failedCallback: Function) {
    emits("uploadCheckImages", checkImages, callback, failedCallback)
}

function cancelFindAgent() {
    emits('cancelPayment');
}

const toChat = () => {
    emits('toChat');
}

const handleReturnToSite = () => {
	emits('returnToSite');
}

onMounted(async () => {
    initTimerService();
});

watch(() => payment.value!.status, () => {
    initTimerService();
});

watch(() => percent.value < 0, (statement) => {
    if(statement)
    emits('timerExpired');
});

const initTimerService = () => {
    if (payment?.value?.status &&
        [
            BuyingOperationStatus.EMPTY,
            BuyingOperationStatus.ACCEPTED,
            BuyingOperationStatus.CREATED,
            BuyingOperationStatus.CASHED,
        ].includes(payment.value.status)) {
        timerService.start(payment.value.status, payment.value.updatedAt!);
    } else {
        timerService.stopIntervals();
    }
}
</script>

<style lang="scss">
.payment-view__content {
    position: relative;

    .wrapper-elements {
        padding: 0 16px;
    }

    .cancel {
        margin-bottom: 16px;
    }
}
</style>
